import { useTranslation } from "react-i18next";

import {
  ActionButton,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSkeleton,
  CardTitle,
  IconBadge,
  typography,
} from "@dsaui/components";
import { cn } from "@dsaui/utils";
import { Identification } from "@dsaui/icons";

export interface PersonalInformationCardProps {
  email: string;
  loading?: boolean;
  mailingDetails: string[];
  name: string;
  phone: string | null;
  updateLabel?: string;
  updateHref: string;
}

export const PersonalInformationCard = ({
  email,
  loading = false,
  mailingDetails,
  name,
  phone,
  updateLabel,
  updateHref,
}: PersonalInformationCardProps) => {
  const { t } = useTranslation();
  const detailLabelClassName = cn(
    typography.profileDetail.className,
    "min-w-0 text-right font-semibold text-gray-900",
  );
  const detailValueClassName = cn(
    typography.profileDetail.className,
    "min-w-0 wrap-anywhere",
  );

  if (loading) return <CardSkeleton className="h-full" rows={4} showBanner={false} />;

  return (
    <Card className="h-full">
      <CardBody className="h-full gap-6">
        <CardHeader className="flex-row items-center">
          <IconBadge>
            <Identification className="size-6" />
          </IconBadge>
          <CardTitle>{t("dashboard.personalInformation.title")}</CardTitle>
        </CardHeader>

        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-4">
          <dt className={detailLabelClassName}>
            {t("dashboard.personalInformation.name")}
          </dt>
          <dd className={cn("m-0", detailValueClassName)}>{name}</dd>

          <dt className={detailLabelClassName}>
            {t("dashboard.personalInformation.email")}
          </dt>
          <dd className={cn("m-0", detailValueClassName)}>{email}</dd>

          <dt className={detailLabelClassName}>
            {t("dashboard.personalInformation.phone")}
          </dt>
          <dd className={cn("m-0", detailValueClassName)}>
            {phone ?? t("dashboard.personalInformation.missingValue")}
          </dd>

          <dt className={detailLabelClassName}>
            {t("dashboard.personalInformation.mailingAddress")}
          </dt>
          <dd className={cn("m-0", detailValueClassName)}>
            {mailingDetails.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </dd>
        </dl>

        <CardFooter>
          <ActionButton href={updateHref}>
            {updateLabel ?? t("dashboard.actions.update")}
          </ActionButton>
        </CardFooter>
      </CardBody>
    </Card>
  );
};

export default PersonalInformationCard;
